/**
 * Showing a stored value in the org's units, and reading a typed one back (units slice 2b,
 * docs/plans/2026-10-06_units-slice-2.md sections 3 and 4). Pure; the hooks that bind them to the org's choice are
 * `useDisplayUnits` and `useUnitFormat` (lib/api/display-units).
 *
 * Two rules hold throughout:
 *   - Storage never changes. A value is converted for the screen, and a typed one is converted back to the unit it is
 *     stored in before it is sent (the server refuses another: unit_mismatch).
 *   - An org that is not asked to convert sees exactly what it saw before. `shownFor` hands back the very same unit
 *     string and the very same numbers when the choice does not convert the unit (metric, no choice yet, time, volume,
 *     an unknown unit), so a page that formats its own way keeps its own way for them.
 */
import { convertBetween, dimensionOf, normalise, UnitConversionError } from "./convert";
import { formatQuantity, type FormatOptions, type Formatted } from "./format";
import { unitFor, type DisplayPrefs } from "./prefs";

/** The unit a stored value is shown in, and the conversion that gets it there. */
export interface Shown {
  /** The unit to write next to the value: the stored one as written when nothing converts. */
  unit: string;
  /** The choice converts this unit; false means every value is shown as stored. */
  converted: boolean;
  /** A stored value in the shown unit (the same number when nothing converts). */
  convert: (stored: number) => number;
  /**
   * What a spread or a difference is multiplied by (1 when nothing converts): a standard deviation, a bias, a band's
   * half-width. Never shifted: 10 °C of spread is 18 °F, not 50. Its square scales a variance.
   */
  scale: number;
}

const SAME = (v: number) => v;

/**
 * The unit a value stored in `storedUnit` is shown in under this choice, with the conversion. With no choice (before
 * the read answers, a page with no live data), an unknown unit, or a unit the choice shows as recorded, the stored
 * unit comes back as written and the conversion changes nothing.
 */
export function shownFor(storedUnit: string, prefs: DisplayPrefs | undefined): Shown {
  const as = { unit: storedUnit, converted: false, convert: SAME, scale: 1 };
  if (!prefs || dimensionOf(storedUnit) === "") return as;
  const shown = unitFor(storedUnit, prefs);
  if (normalise(shown) === normalise(storedUnit)) return as;
  const convert = (v: number) => convertBetween(v, storedUnit, shown);
  return { unit: shown, converted: true, convert, scale: convert(1) - convert(0) };
}

/**
 * A series of readings in the shown unit, a gap (null) staying a gap; the same list when nothing converts, so a metric
 * org's chart gets the object it always got.
 */
export function shownSeries<T extends { v: number | null }>(
  points: readonly T[],
  storedUnit: string,
  prefs: DisplayPrefs | undefined,
): { points: readonly T[]; unit: string; converted: boolean } {
  const s = shownFor(storedUnit, prefs);
  if (!s.converted) return { points, unit: s.unit, converted: false };
  return {
    points: points.map((p) => (p.v === null || !Number.isFinite(p.v) ? p : { ...p, v: s.convert(p.v) })),
    unit: s.unit,
    converted: true,
  };
}

/**
 * A spread (a sigma, a half-width) in the shown unit: scaled, never shifted. `around` is a value the spread sits
 * about, in the stored unit, so the answer is right for an affine unit (10 °C either side of 1000 °C is 18 °F either
 * side of 1832 °F).
 */
export function spreadOf(spread: number, around: number, storedUnit: string, prefs: DisplayPrefs | undefined): number {
  const s = shownFor(storedUnit, prefs);
  if (!s.converted) return spread;
  return s.convert(around + spread) - s.convert(around);
}

/**
 * A key that moves whenever the units a form shows move: "stored" with no choice, else the system and the overrides in
 * a fixed order. A form keyed by it starts again on a change, so nothing typed in one unit is sent as another (#1190
 * review MEDIUM 6: a number typed before the org's units are read).
 */
export function shownKey(prefs: DisplayPrefs | undefined): string {
  if (!prefs) return "stored";
  const overrides = Object.entries(prefs.overrides ?? {})
    .filter(([, unit]) => unit)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([dim, unit]) => `${dim}=${unit}`);
  return [prefs.system, ...overrides].join("|");
}

/** `formatQuantity` bound to a choice: `(value, storedUnit) => {text, value, unit}`, for code that is not a hook. */
export function formatWith(prefs: DisplayPrefs): (value: number, storedUnit: string, opts?: FormatOptions) => Formatted {
  return (value, storedUnit, opts) => formatQuantity(value, storedUnit, prefs, opts);
}

/** A converted value written the way a person would type it: ten significant figures, so 1449.9999999999998 is 1450. */
const tidy = (v: number) => Number(v.toPrecision(10));

/**
 * What a person typed in the shown unit, as the stored unit holds it. The same number bit for bit when the two units
 * are the same; otherwise converted and tidied to ten figures. Null when it cannot be done (not a number, an unknown
 * unit, two dimensions): the caller says so and sends nothing.
 */
export function typedToStored(typed: number, shownUnit: string, storedUnit: string): number | null {
  if (!Number.isFinite(typed)) return null;
  if (normalise(shownUnit) === normalise(storedUnit)) return typed;
  try {
    return tidy(convertBetween(typed, shownUnit, storedUnit));
  } catch (err) {
    if (err instanceof UnitConversionError) return null;
    throw err;
  }
}

export interface BackToStored {
  /** The number now in the field, in the shown unit. */
  typed: number;
  shownUnit: string;
  storedUnit: string;
  /** What the field held when the form opened, in the stored unit. */
  stored: number;
  /** What the field showed when the form opened (the stored value in the shown unit, as written), in the shown unit. */
  shownOfStored: number;
}

/**
 * A field's value for the request. A field the person did not touch (it still holds the number it opened with) sends
 * its stored value exactly as it was, never a round trip that drifts 1450 to 1449.99; an edited one is converted back
 * with `typedToStored`. Null when an edited one cannot be converted.
 */
export function backToStored(a: BackToStored): number | null {
  if (a.typed === a.shownOfStored) return a.stored;
  return typedToStored(a.typed, a.shownUnit, a.storedUnit);
}

/**
 * A stored value written for a file in the shown unit: the fewest figures that read back to the stored value exactly
 * (1380 °C is 2516 °F, not 2516.0000000001), so an exported file imported unchanged leaves every stored value as it was.
 * The stored value itself where nothing converts.
 */
export function shownForFile(value: number, storedUnit: string, prefs: DisplayPrefs | undefined): number {
  const s = shownFor(storedUnit, prefs);
  if (!s.converted || !Number.isFinite(value)) return value;
  const shown = s.convert(value);
  const want = tidy(value);
  for (let places = 4; places <= 12; places++) {
    const candidate = Number(shown.toPrecision(places));
    if (typedToStored(candidate, s.unit, storedUnit) === want) return candidate;
  }
  return Number(shown.toPrecision(12));
}
